/**
 * ─── THE TWO NAMES ──────────────────────────────────────────────────────────
 *
 * The client's feedback of 22 September 2026 (`Docs/From Client/Avexa
 * Marketplace - Feedback.docx` § 3), verbatim:
 *
 *   "First, both the website and the app need a different name, that i will
 *    decide soon. They should not use the Avexa Solutions name, as Avexa
 *    Solutions should remain the name of the company behind the product."
 *
 * So there are TWO names, and the single most expensive mistake available here
 * is to treat them as one:
 *
 *   • the PRODUCT — the marketplace, the website, the mobile app. Changes to a
 *     name nobody knows yet. Every user-facing string that means "the platform
 *     you are using" points at `productName`.
 *
 *   • the COMPANY — Avexa Solutions. Does NOT change. It is the copyright
 *     holder, the party to the terms, the data controller under the GDPR, and
 *     the subject of the separate corporate website the same document asks for.
 *
 * ─── HOW A STRING REACHES THESE VALUES ──────────────────────────────────────
 *
 * Not by importing this file into a component. Almost every occurrence is
 * inside a translation catalogue, so the catalogues carry a PLACEHOLDER and
 * i18next fills it:
 *
 *   en.json   "home.why.eyebrow": "Why {{product}}"
 *   nb.json   "home.why.eyebrow": "Derfor {{product}}"
 *
 * `brandInterpolation` below is handed to i18next as
 * `interpolation.defaultVariables`, which supplies these to EVERY `t()` call
 * without a single call site passing them. `apps/web/src/i18n.ts` and
 * `apps/mobile/src/i18n.ts` each do that in one line.
 *
 * ⚠️ `defaultVariables` is per-i18next-instance, not global. A fourth surface
 * that calls `i18next.init()` of its own must wire it too, or its `{{product}}`
 * renders as the literal text `{{product}}` on screen.
 *
 * ─── WHAT IS DELIBERATELY NOT IN HERE ───────────────────────────────────────
 *
 * Nothing that is not actually read. In particular there is no domain, no
 * support address, no store URL and no legal-entity suffix: `avexa.no` and
 * `avexamarketplace.no` are both NXDOMAIN (CLAUDE.md § 7), the support mailbox
 * is `app_settings['support.email']` and belongs to the admin rather than to
 * code, and nobody has told us whether the company is "Avexa Solutions AS".
 * Inventing a field here would just be a second place to be wrong.
 *
 * The rename-day checklist — everything the new name touches that is NOT a
 * string in this repository — is `Docs/26-RENAME.md`.
 */

export interface Brand {
  /**
   * The product, as it is spoken. "the {{product}} app", "Businesses on
   * {{product}}", "Why {{product}}". This is the one that changes.
   */
  readonly productName: string
  /**
   * The product's formal name, used where the sentence is naming a thing
   * rather than referring to one: `<title>`, `og:site_name`, the web app
   * manifest, and the two legal sentences that identify what the terms are
   * about ("an agreement between you and the company operating …").
   *
   * ⚠️ IT IS A SEPARATE FIELD ON PURPOSE, AND IT IS A RENAME-DAY DECISION.
   * Today it is `productName` plus the word "Marketplace". Whether the new
   * name takes a suffix at all is not ours to assume — if it does not, this
   * becomes the same string as `productName` and the copy still reads
   * correctly. Collapsing the two now would silently shorten eleven strings.
   */
  readonly productFullName: string
  /**
   * The company behind the product. **Does not change.** Copyright line, the
   * party to the terms, the GDPR controller, the corporate website.
   */
  readonly companyName: string
}

export const brand: Brand = {
  productName: 'Proffplassen',
  productFullName: 'Proffplassen',
  companyName: 'Avexa Solutions',
}

/**
 * ─── THE NAME ARRIVED ON 6 OCTOBER 2026 ─────────────────────────────────────
 *
 * Not in the document that was supposed to carry it. `Feedback from
 * Hassan.docx` § 1.2 is headed "Product Name & Domain", states that both have
 * now been decided, and leaves both fields reading literally "[To be
 * inserted]". The name arrived in a message instead, as a fact about something
 * else entirely:
 *
 *   "I have prepared the Terms and Conditions and Privacy Policy documents
 *    for Proffplassen."
 *
 * Corroborated twice in the same batch — he writes "Pay with Proffplassen" and
 * "Proffplassen+ members", i.e. he has already renamed the escrow feature and
 * the paid tier in his own head. This is the third decision this project has
 * found inside an attachment about a different subject.
 *
 * ─── WHY `productFullName` LOST ITS SUFFIX ──────────────────────────────────
 *
 * It was "Avexa Marketplace" — `productName` plus a describing word, because
 * "Avexa" alone names nothing in particular. The comment above called the
 * suffix a rename-day decision and said that if the new name does not take
 * one, this field simply becomes `productName` and the copy still reads.
 *
 * It does not take one. "Proffplassen" is already a complete Norwegian noun
 * phrase — *proff* + *plassen*, "the professionals' place", with the definite
 * article built into the word. "Proffplassen Marketplace" would both repeat
 * itself and weld an English common noun onto a Norwegian definite form, which
 * is exactly the sort of thing a Norwegian reader notices and we would not.
 *
 * So the two fields are now the same string, deliberately, and the field stays
 * rather than being deleted: it marks the eleven places where the sentence is
 * NAMING the product rather than referring to it, and a future name may want
 * the distinction back.
 *
 * ⚠️ The wordmark is eleven characters where AVEXA was five.
 * (`productUpper` no longer uppercases — the client's artwork is sentence case; see below.) Anywhere the mark sits in a tight
 * space — the mobile header, the onboarding wordmark, the web nav at phone
 * width, the app icon — needs looking at, not assuming. Norwegian copy was
 * already the long case for layout; the brand just joined it.
 *
 * ⚠️ THE CODE IS THE CHEAP HALF, AND IT IS NOW DONE. What is not done is
 * everything in `Docs/26-RENAME.md`: the domain, both store listings, Apple's
 * week-long account verification, the `avexa://` deep-link scheme in
 * Supabase's redirect allow-list, the EAS project, and both Netlify sites.
 * Those have lead times measured in days and are the reason the name was the
 * largest blocker in the project.
 */

/**
 * The values i18next substitutes into `{{…}}`, for
 * `interpolation.defaultVariables`.
 *
 * `productUpper` is DERIVED, not authored: the client's onboarding mockups set
 * the wordmark in capitals ("Join AVEXA", "Enter AVEXA", "AVEXA+"), and a
 * second hand-written field would be a second thing to forget on rename day.
 * It is not a CSS `text-transform` because the string is the brand, not a
 * styling choice — a screen reader should say the name, and Norwegian copy
 * around it stays sentence case.
 *
 * ⚠️ These four names are reserved. `scripts/check-locales.mjs` compares
 * placeholders between `en` and `nb`, so a typo in one catalogue is caught —
 * but a name that COLLIDES with a real variable is not. Checked against every
 * placeholder in all five catalogues before they were chosen: `product`,
 * `productUpper`, `productFull` and `company` were all unused. The near misses
 * to avoid are `name`, `title`, `business` and `page`, which are all live.
 */
export const brandInterpolation = {
  product: brand.productName,
  /**
   * ⚠️ **NO LONGER UPPERCASED, 6 Oct 2026 — the client's own artwork decided this.**
   *
   * It was `productName.toUpperCase()`, because the Avexa onboarding mockups set the wordmark in
   * capitals ("Join AVEXA", "AVEXA+"). **The Proffplassen logo he sent sets it in sentence case**,
   * in both the light and the dark file. So twelve copy strings that say `{{productUpper}}` were
   * about to render "Join PROFFPLASSEN" and "PROFFPLASSEN+" against a logo that says
   * Proffplassen — off-brand, and shouty at eleven characters where five were tolerable.
   *
   * **The field stays rather than being deleted, and the twelve strings were not rewritten.**
   * Changing it here is one line and reversible; rewriting the strings is a change in three
   * locale catalogues in two languages that would have to be undone the same way. If a future
   * name IS a capitalised wordmark, restore the `.toUpperCase()` and every string is correct
   * again — which is exactly the property this indirection exists for.
   */
  productUpper: brand.productName,
  productFull: brand.productFullName,
  company: brand.companyName,
} as const

export type BrandInterpolation = typeof brandInterpolation
